import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knoxApi } from '@/lib/knox-api';
import {
  loadFeishuSDK,
  requestFeishuAuthCode,
} from '@/lib/utils/feishu-browser';

/**
 * 飞书客户端内 JSAPI 免登。
 *
 * 在飞书客户端内打开时自动触发：加载飞书 H5 SDK → requestAccess/requestAuthCode
 * 拿免登 code → POST /oauth/feishu/jsapi-login 换会话 → 全量刷新到 /workspaces。
 *
 * 全量刷新是为了复用 AuthProvider/LoginPage 现有的"有组织→workspace / 无组织→org-binding"
 * 链路，本组件不自己判断组织状态。
 *
 * 参考: ai-workbench (Javis) components/auth/FeishuJSAPI.tsx，改造点：
 *  - 用 knox knoxApi.feishuJsapiLogin（非裸 fetch）
 *  - requestAccess 优先 + errno 103 降级 requestAuthCode（飞书官方推荐）
 */
export function FeishuAutoLogin({ appId }: { appId: string }) {
  const { t } = useTranslation('knox-aiops');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef(false);

  useEffect(() => {
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        await loadFeishuSDK();
        const code = await requestFeishuAuthCode(appId);
        if (cancelled) return;

        await knoxApi.feishuJsapiLogin('feishu', code);
        if (cancelled) return;

        // 全量刷新，让 AuthProvider 重新拉取会话并走"有组织→workspace / 无组织→org-binding"。
        // 用 replace 避免回退按钮回到免登页重新触发。
        window.location.replace('/workspaces');
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : '飞书免登失败';
        setError(msg);
        setLoading(false);
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [appId]);

  return (
    <div className="flex flex-col items-center gap-3 py-8">
      {loading && (
        <>
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-primary" />
          <p className="text-sm text-muted-foreground">{t('login.feishuAutoLoggingIn', '正在通过飞书登录...')}</p>
        </>
      )}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
